import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";

export interface ComposerProps {
  disabled?: boolean;
  /** Whether to offer the attach control at all — the bot's own setting, relayed on `session.ready`. */
  allowImages?: boolean;
  /** Put the caret in the message field as soon as this mounts — which, for a panel that only exists while it's open, is the moment the widget opens. */
  autoFocus?: boolean;
  /** One message carrying both, WhatsApp-style: the caption and the photo it is about travel together. */
  onSend: (text: string, files: File[]) => void;
  onNewConversation: () => void;
}

/** Six is the server's cap too; rejecting here means the shopper finds out while picking rather than after sending. */
const MAX_FILES = 6;

/**
 * How tall the field is allowed to grow before it starts scrolling itself.
 * Five lines is enough to see a whole pasted address or order number in one
 * go; past that the transcript above would be squeezed to nothing, which is
 * the more expensive loss in a 360px panel.
 */
const MAX_ROWS = 5;

/** Only used when the computed line-height is unusable (`normal`, or a test DOM that reports nothing). */
const FALLBACK_LINE_HEIGHT = 20;

/**
 * A soft keyboard has no Shift to hold, so on a phone Enter cannot be both
 * the send and the line break — and a shopper who can only ever send one
 * line is the thing this field exists to stop. There, Enter breaks the line
 * and the Send button beside it does the sending; on a hardware keyboard
 * Enter sends, as chat has always done. `pointer: coarse` is the same signal
 * the stylesheet keys its anti-zoom rule off.
 */
function hasSoftKeyboard(): boolean {
  return typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
}

/**
 * A textarea does not size itself to its content, so it is measured and set
 * on every change: collapse to `auto` to get an unpadded `scrollHeight`, then
 * take the smaller of that and `MAX_ROWS` lines. Past the cap the element
 * scrolls; below it, it never does — a scrollbar on a two-line box is the
 * thing that makes an auto-growing field look broken.
 */
function fitToContent(el: HTMLTextAreaElement): void {
  el.style.height = "auto";
  const style = typeof getComputedStyle === "function" ? getComputedStyle(el) : undefined;
  const lineHeight = parseFloat(style?.lineHeight ?? "") || FALLBACK_LINE_HEIGHT;
  const padding = (parseFloat(style?.paddingTop ?? "") || 0) + (parseFloat(style?.paddingBottom ?? "") || 0);
  const border = (parseFloat(style?.borderTopWidth ?? "") || 0) + (parseFloat(style?.borderBottomWidth ?? "") || 0);
  // `scrollHeight` spans content plus padding; `height` under the widget's
  // border-box sizing spans those plus the border, so the cap is built in
  // scrollHeight's terms and the border added only when it is written back.
  const cap = lineHeight * MAX_ROWS + padding;
  const content = Math.min(el.scrollHeight, cap);
  el.style.height = `${content + border}px`;
  el.style.overflowY = el.scrollHeight > cap ? "auto" : "hidden";
}

interface Pending {
  file: File;
  /** Local preview, revoked on removal — an object URL that outlives its use is a leak in a widget that stays open all day. */
  url: string;
}

export function Composer(props: ComposerProps) {
  const [text, setText] = useState("");
  const [pending, setPending] = useState<Pending[]>([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const textInput = useRef<HTMLTextAreaElement>(null);

  /**
   * Someone who taps the launcher has already decided to type. Doing it in a
   * layout effect rather than `useEffect` keeps the `focus()` as close to the
   * tap that caused it as a render will allow, which is what mobile Safari
   * weighs when deciding whether to raise its keyboard — after paint it is
   * reliably too late, and the shopper gets a caret they then have to tap
   * again to use.
   *
   * `preventScroll` because the panel is fixed and the field is at the bottom
   * of it: without it the host page can jump to "reveal" an element that was
   * never out of view.
   */
  useLayoutEffect(() => {
    if (!props.autoFocus || props.disabled) return;
    textInput.current?.focus({ preventScroll: true });
  }, [props.autoFocus, props.disabled]);

  /**
   * Re-measured on every change to `text`, which covers the lot: typing, a
   * multi-line paste, and the reset back to one row after a send. In a layout
   * effect so the new height is in place before the browser paints — sizing
   * after paint is a visible jump on every keystroke that wraps.
   */
  useLayoutEffect(() => {
    const el = textInput.current;
    if (el) fitToContent(el);
  }, [text]);

  // A menu that only closes via its own button is a menu people leave open
  // by accident and then click through.
  useEffect(() => {
    if (!menuOpen) return;
    function dismiss(event: MouseEvent) {
      // The widget mounts inside a shadow root, so an event that originated
      // inside it arrives at this `document` listener retargeted — `target`
      // reads as the shadow host, never the button that was actually
      // clicked. `composedPath()` carries the real, un-retargeted path, so
      // that's what has to be checked here; without it every mousedown,
      // including the one on the menu item itself, looks like it landed
      // outside the menu and closes it before the click can fire.
      const path = typeof event.composedPath === "function" ? event.composedPath() : [event.target as Node];
      if (!path.some((node) => node === menuRef.current)) setMenuOpen(false);
    }
    document.addEventListener("mousedown", dismiss);
    return () => document.removeEventListener("mousedown", dismiss);
  }, [menuOpen]);

  // Removal revokes its own url, and a sent message inherits the ones it
  // renders — so nothing is revoked here. A widget torn down mid-compose
  // leaks at most a handful of urls, and the page is going away anyway.

  function addFiles(files: FileList | null) {
    if (!files) return;
    const images = Array.from(files).filter((file) => file.type.startsWith("image/"));
    setPending((current) => {
      const room = MAX_FILES - current.length;
      if (room <= 0) return current;
      return [...current, ...images.slice(0, room).map((file) => ({ file, url: URL.createObjectURL(file) }))];
    });
  }

  function removeAt(index: number) {
    setPending((current) => {
      const item = current[index];
      if (item) URL.revokeObjectURL(item.url);
      return current.filter((_, position) => position !== index);
    });
  }

  function send() {
    // Only the ends: the blank lines a shopper put *between* two paragraphs
    // are part of what they wrote, and pasted text keeps the shape it had.
    const trimmed = text.trim();
    // A photo on its own is a message; an empty box is not.
    if (!trimmed && pending.length === 0) return;
    props.onSend(trimmed, pending.map((item) => item.file));
    setText("");
    // Not revoked here: the sent message renders these same urls as its own
    // preview, so they have to outlive the composer that made them.
    setPending([]);
  }

  function handleSubmit(event: Event) {
    event.preventDefault();
    send();
  }

  /**
   * On a hardware keyboard Enter sends and Shift+Enter breaks the line — the
   * chat convention, and the send path a keyboard now needs at all: a
   * textarea does not submit its form on Enter the way the `<input>` this
   * replaced did. Any other modifier is left to the browser rather than read
   * as a second way to send, and a soft keyboard is `hasSoftKeyboard`'s case.
   */
  function handleKeyDown(event: KeyboardEvent) {
    if (event.key !== "Enter" || event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) return;
    // Mid-composition Enter is choosing a candidate, not sending: every IME
    // user typing Japanese or Chinese would otherwise send a fragment on
    // their first keystroke. `keyCode === 229` is the older browsers' tell
    // for the same state.
    if (event.isComposing || event.keyCode === 229) return;
    // Read per keystroke rather than at mount: a tablet can be docked and
    // undocked, and this is cheap next to what a keypress already costs.
    if (hasSoftKeyboard()) return;
    event.preventDefault();
    send();
  }

  return (
    <form class="cos-composer" onSubmit={handleSubmit}>
      {pending.length > 0 && (
        <div class="cos-attachments">
          {pending.map((item, index) => (
            <div class="cos-attachment" key={item.url}>
              <img src={item.url} alt="" />
              <button type="button" aria-label="Remove image" onClick={() => removeAt(index)}>
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <div class="cos-composer-row">
        <div class="cos-menu" ref={menuRef}>
          <button
            type="button"
            class="cos-icon-button"
            aria-label="More options"
            aria-haspopup="menu"
            aria-expanded={menuOpen}
            title="More options"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            ⋮
          </button>
          {menuOpen && (
            <div class="cos-menu-popover" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setMenuOpen(false);
                  props.onNewConversation();
                }}
              >
                Start a new conversation
              </button>
            </div>
          )}
        </div>

        {props.allowImages && (
          <>
            <input
              ref={fileInput}
              type="file"
              accept="image/*"
              multiple
              class="cos-file-input"
              onChange={(event) => {
                addFiles((event.currentTarget as HTMLInputElement).files);
                // Cleared so picking the same file twice still fires.
                (event.currentTarget as HTMLInputElement).value = "";
              }}
            />
            <button
              type="button"
              class="cos-icon-button"
              aria-label="Attach an image"
              title="Attach an image"
              disabled={props.disabled || pending.length >= MAX_FILES}
              onClick={() => fileInput.current?.click()}
            >
              +
            </button>
          </>
        )}

        <textarea
          ref={textInput}
          rows={1}
          value={text}
          // A soft keyboard offers a return key rather than a "send" one,
          // which is what Enter now does there.
          enterkeyhint="enter"
          placeholder="Type a message…"
          aria-label="Message"
          disabled={props.disabled}
          onKeyDown={handleKeyDown}
          onInput={(event) => setText((event.currentTarget as HTMLTextAreaElement).value)}
        />
        <button type="submit" disabled={props.disabled || (text.trim() === "" && pending.length === 0)}>
          Send
        </button>
      </div>
    </form>
  );
}
